import { useEffect } from 'react';
import { PlatformEnvironment } from '@mews/enums';
import { useLoggerService } from '@mews/framework';
import { GDProduct, MewsReportingTracking, MewsReportingTrackingEvents } from '../const';
import { useMeasurePerformance } from '../hooks';
import { CustomFilterContext, GoodDataParameters } from '../types';
import { getWorkspaceId } from '../utils';
import { type GDC, GoodDataEmbed } from './GoodDataEmbed';

interface GoodDataDashboardProps {
    customFilterContext?: CustomFilterContext;
    dashboard: string;
    environment: PlatformEnvironment;
    goodDataParameters: GoodDataParameters;
    hideTopBar?: boolean;
    product: GDProduct;
    withLoader?: boolean;
    workspaceId?: string;
}

export const GoodDataDashboard = ({
    customFilterContext,
    dashboard,
    environment,
    goodDataParameters,
    hideTopBar = true,
    product,
    withLoader,
    workspaceId,
}: GoodDataDashboardProps) => {
    const workspace = getWorkspaceId({ environment, goodDataParameters, product, workspaceId });
    const logger = useLoggerService();
    const {
        startTracking: startRenderTracking,
        endTracking: endRenderTracking,
        getTimeToLoad: getTimeToRender,
    } = useMeasurePerformance();
    const { startTracking: startLoadTracking, endTracking: endLoadTracking, getTimeToLoad } = useMeasurePerformance();

    useEffect(() => {
        startRenderTracking();
        startLoadTracking();
    }, [startRenderTracking, startLoadTracking]);

    const handleMessage = (gdc: GDC) => {
        const messageName = gdc.event.name;

        if (messageName === 'listeningForApiToken') {
            endRenderTracking();
            const timeToRender = getTimeToRender();
            logger.log(MewsReportingTracking, MewsReportingTrackingEvents.EmbeddedReportRendered, {
                dashboard,
                timeToRender,
                workspaceId: workspace,
            });
        }

        if (messageName === 'loaded') {
            endLoadTracking();
            const timeToLoad = getTimeToLoad();
            logger.log(MewsReportingTracking, MewsReportingTrackingEvents.EmbeddedReportLoaded, {
                dashboard,
                timeToLoad,
                workspaceId: workspace,
            });
        }

        if (messageName === 'filterContextChanged') {
            logger.log(MewsReportingTracking, MewsReportingTrackingEvents.EmbeddedReportFilterChanged, {
                dashboard,
                filters: gdc.event.data.filters,
                workspaceId: workspace,
            });
        }
    };

    return (
        <GoodDataEmbed
            customFilterContext={customFilterContext}
            goodDataParameters={goodDataParameters}
            hideTopBar={hideTopBar}
            onMessage={handleMessage}
            path={`dashboards/embedded/#/workspace/${workspace}/dashboard/${dashboard}`}
            withLoader={withLoader}
        />
    );
};
